/**
 * "Available offline" pins (spec §8.13, §16.1): a pinned page keeps its Loro snapshot (already
 * persisted by the doc manager) and its referenced media in OPFS.
 */
import type { LoroDoc } from "loro-crdt";
import type { AssetRef, SnapshotRef } from "@kss/schema";
import { getMeta, setMeta } from "../sync/persistence";
import { getBlob, putBlob } from "./opfsBlobCache";

const KEY = "pinnedPages";

export async function pinnedPages(): Promise<Set<string>> {
  return new Set((await getMeta<string[]>(KEY)) ?? []);
}

export async function setPinned(docId: string, pinned: boolean): Promise<void> {
  const cur = await pinnedPages();
  if (pinned) cur.add(docId);
  else cur.delete(docId);
  await setMeta(KEY, [...cur]);
}

export function assetHashesOf(doc: LoroDoc): string[] {
  const out = new Set<string>();
  for (const n of doc.getTree("blocks").getNodes({ withDeleted: false })) {
    const body = n.data.get("body");
    if (!body || typeof body !== "object" || !("get" in body)) continue;
    const get = (k: string) => (body as { get(k: string): unknown }).get(k);
    const asset = get("asset") as AssetRef | null;
    if (asset?.hash) out.add(asset.hash);
    // web page block (spec §8.16): the frozen snapshot and every one in history, so it opens offline
    const frozen = get("frozen") as SnapshotRef | null | undefined;
    if (frozen?.asset?.hash) out.add(frozen.asset.hash);
    const snapshots = get("snapshots") as { toJSON?(): Record<string, SnapshotRef> } | undefined;
    for (const ref of Object.values(snapshots?.toJSON?.() ?? {})) if (ref?.asset?.hash) out.add(ref.asset.hash);
  }
  return [...out];
}

/** Page ids of the originals of every mirror block on the page (spec §8.18). */
export function mirrorSourcePagesOf(doc: LoroDoc): string[] {
  const out = new Set<string>();
  for (const n of doc.getTree("blocks").getNodes({ withDeleted: false })) {
    if (n.data.get("type") !== "mirror") continue;
    const body = n.data.get("body") as { get?(k: string): unknown } | undefined;
    const source = body?.get?.("source") as { iri?: string } | null | undefined;
    const m = source?.iri && /^urn:km:(?:blk|doc):([0-9a-f-]{36})/.exec(source.iri);
    if (m) out.add(m[1]!);
  }
  return [...out];
}

/**
 * Download every referenced original into OPFS (skips those already present). Mirrors are followed to
 * their original pages (each page once, recursively): opening one persists its snapshot, and its
 * assets are cached too, so the mirrors show offline (spec §8.13, §8.18).
 */
export async function cachePageAssets(doc: LoroDoc, onProgress?: (done: number, total: number) => void): Promise<void> {
  const { openSettled } = await import("../blocks/clipboard/sources");
  const seen = new Set<string>();
  const docs: LoroDoc[] = [doc];
  const release: Array<() => void> = [];
  try {
    for (let i = 0; i < docs.length && i < 50; i++) {
      for (const pageId of mirrorSourcePagesOf(docs[i]!)) {
        if (seen.has(pageId)) continue;
        seen.add(pageId);
        const h = await openSettled(pageId);
        release.push(() => h.release());
        if (h.doc !== doc) docs.push(h.doc);
      }
    }
    const hashes = [...new Set(docs.flatMap(assetHashesOf))];
    await downloadAll(hashes, onProgress);
  } finally {
    release.forEach((r) => r());
  }
}

async function downloadAll(hashes: string[], onProgress?: (done: number, total: number) => void): Promise<void> {
  let done = 0;
  for (const h of hashes) {
    if (!(await getBlob(h))) {
      const r = await fetch(`/api/assets/${h}`, { credentials: "same-origin" });
      if (r.ok) await putBlob(h, await r.blob());
    }
    onProgress?.(++done, hashes.length);
  }
}
